import React from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Brand-coloured pin (avoids Leaflet's broken default marker image paths under bundlers)
const goldPin = L.divIcon({
  className: "asa-map-pin",
  html: `<span style="
    display:block;width:22px;height:22px;border-radius:50% 50% 50% 0;
    transform:rotate(-45deg);background:#D4A94A;border:2px solid #12100e;
    box-shadow:0 3px 8px rgba(0,0,0,0.5);"></span>`,
  iconSize: [22, 22],
  iconAnchor: [11, 22],
  popupAnchor: [0, -22],
});

const esc = (s) =>
  String(s || "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

export const MuseumsMap = ({ museums }) => {
  const mapRef = React.useRef(null);
  const containerRef = React.useRef(null);

  const pins = React.useMemo(
    () => museums.filter((m) => typeof m.lat === "number" && typeof m.lng === "number"),
    [museums]
  );

  React.useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current, { scrollWheelZoom: false, attributionControl: true }).setView([7.6, 4.2], 7);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 18,
      attribution: '&copy; OpenStreetMap contributors',
    }).addTo(map);
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  React.useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const layer = L.layerGroup().addTo(map);
    const bounds = [];
    pins.forEach((m) => {
      const popup = `
        <div style="min-width:180px;font-family:system-ui,sans-serif">
          <div style="font-size:11px;letter-spacing:1px;text-transform:uppercase;color:#A6603A;margin-bottom:2px">${esc(m.type)}</div>
          <div style="font-weight:700;font-size:15px;color:#1b1712;margin-bottom:2px">${esc(m.name)}</div>
          <div style="font-size:12px;color:#5c5346;margin-bottom:6px">${esc(m.location)}</div>
          ${m.website ? `<a href="${esc(m.website)}" target="_blank" rel="noopener noreferrer" style="font-size:12px;font-weight:600;color:#1b3327">Visit website →</a>` : ""}
        </div>`;
      const marker = L.marker([m.lat, m.lng], { icon: goldPin }).bindPopup(popup);
      marker.addTo(layer);
      bounds.push([m.lat, m.lng]);
    });
    if (bounds.length) map.fitBounds(bounds, { padding: [50, 50], maxZoom: 9 });
    return () => layer.remove();
  }, [pins]);

  return (
    <div
      ref={containerRef}
      data-testid="museums-map"
      className="w-full rounded-2xl overflow-hidden border border-cream/15"
      style={{ height: 560, background: "#12100e", zIndex: 0 }}
    />
  );
};

export default MuseumsMap;
